/* eslint-disable no-undef */
/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/**
 * This component is responsible for drawing the MPT Flight Path
 */
import { useEffect, useState } from 'react';
import { Feature } from '@turf/helpers';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import { withTheme } from 'styled-components';
import FlightLeg from '../../../shared/entities/flightLeg';
import FlightLegServiceAlerts from '../../../shared/entities/flightLegServiceAlerts';
import { getFlightPathSegments } from '../../../utils/MptFlightPathSegmentingUtils';

export interface FlightPathContainerStateProps {}

// a Flight Path Waypoint is an element in the flight path array returned from /terminals/:datapathUuid/flightpath
export interface FlightPathWaypoint {
  timeSinceTakeoffMin: number; // The dateTime that the VMT Periodic Message was sent in unix time (seconds since epoch)
  latitude: number; // The latitude in degrees of where the terminal was when the VMT Periodic Message was sent
  longitude: number; // The longitude in degrees of where the terminal was when the VMT Periodic Message was sent
}

export interface FlightPathContainerProps {
  map: google.maps.Map;
  flightLeg: FlightLeg;
  flightLegServiceAlerts: { [flightLegId: number]: FlightLegServiceAlerts };
  overlaidFlightLegHidden?: boolean;
  addBeamToMap: (name: string, networkType: string, mapType: string) => {};
}

const MptFlightPathContainer = ({
  map,
  flightLeg,
  flightLegServiceAlerts,
  overlaidFlightLegHidden,
  addBeamToMap,
}: FlightPathContainerProps) => {
  // The flightPathSegmentPolylines is the array of Polylines that are linked up to form the multicolored MPT Flight Path
  const [flightPathSegmentPolylines, setFlightPathSegmentPolylines] = useState<google.maps.Polyline[]>([]);

  useEffect(() => {
    if (
      !isNil(flightLeg.id) &&
      !isNil(flightLeg) &&
      !isNil(flightLegServiceAlerts) &&
      !isNil(flightLegServiceAlerts[flightLeg.id]) &&
      !overlaidFlightLegHidden
    ) {
      // draw the fligth leg

      if (flightLeg) {
        if (size(flightLegServiceAlerts[flightLeg.id].beams) && size(flightLegServiceAlerts[flightLeg.id].waypoints)) {
          // Take in the processed flight path waypoints (an array of arrays of online segments) and render the corresponding
          // Polylines on the map
          if (size(flightLegServiceAlerts[flightLeg.id].beams)) {
            flightLegServiceAlerts[flightLeg.id].beams.features.forEach((feature: Feature) => {
              addBeamToMap(
                feature.properties?.name, 
                feature.properties?.network, 
                feature.properties?.mapType
              );
            });
          }

          setFlightPathSegmentPolylines(getFlightPathSegments(flightLegServiceAlerts[flightLeg.id]));
        }
      }
    } else if (overlaidFlightLegHidden) {
      setFlightPathSegmentPolylines([]);
    }
  }, [flightLeg.id, flightLeg, flightLegServiceAlerts, overlaidFlightLegHidden, addBeamToMap]);

  useEffect(() => {
    flightPathSegmentPolylines.forEach((flightPathSegmentPolyline: google.maps.Polyline) => {
      flightPathSegmentPolyline.setMap(map);
    });

    return () => {
      flightPathSegmentPolylines.forEach((flightPathPolyline: google.maps.Polyline) => {
        flightPathPolyline.setMap(null);
      });
    };
  }, [flightPathSegmentPolylines, map]);

  return null;
};

export default withTheme(MptFlightPathContainer);
